<script lang="ts">
  /**
   * The end of your innings in a 1v1.
   *
   * Not the result screen — in an async match there is no result yet. Either
   * you have just set a target and the opponent has to chase it, or you have
   * just finished chasing and the room is about to declare a winner.
   *
   * The submission is an explicit tap rather than automatic, because it is the
   * one moment worth a beat: the score is final, and the next thing that
   * happens is out of the player's hands.
   */
  import type { Match } from '../lib/match.svelte';
  import { figures, oversFrom } from '../lib/commentary';

  interface Props {
    match: Match;
    busy: boolean;
    onsubmit: () => void;
  }
  let { match, busy, onsubmit }: Props = $props();

  const chasing = $derived(match.solo?.inningsIndex === 1);
  const superOver = $derived(match.solo?.superOver === true);
  const result = $derived(chasing ? match.secondInnings : match.firstInnings);
  const chased = $derived(
    chasing && match.solo?.target !== undefined && (result?.runs ?? 0) >= match.solo.target,
  );

  const top = $derived(
    result ? [...result.batting].filter((b) => b.balls > 0).sort((a, b) => b.runs - a.runs)[0] : null,
  );
  const best = $derived(
    result
      ? [...result.bowling]
          .filter((b) => b.balls > 0)
          .sort((a, b) => b.wickets - a.wickets || a.runs - b.runs)[0]
      : null,
  );
</script>

<section class="screen active" aria-label="Innings complete">
  <div class="body" style="text-align:center">
    {#if result}
      <div class="verdict">
        <div class="big tabular">{result.runs}/{result.wickets}</div>
        <p class="muted">
          {match.youName} · {superOver ? `${result.legalBalls} balls` : `${oversFrom(result.legalBalls)} overs`}
        </p>
      </div>

      <div class="card" style="text-align:left">
        {#if chasing}
          <p style="margin:0;font-size:var(--text-sm)">
            {#if chased}
              You got there. The room will call it once it has both innings.
            {:else}
              You fell short of {match.solo?.target}. The room will call it once it has both innings.
            {/if}
          </p>
        {:else}
          <p class="eyebrow" style="margin:0 0 3px">
            {superOver ? 'Super over set' : 'Target set'}
          </p>
          <p style="margin:0">
            <strong class="tabular">{result.runs + 1}</strong>
            {superOver ? 'to beat off one over.' : 'to beat.'} Now it is
            {match.opponent.name}'s turn.
          </p>
        {/if}
      </div>

      {#if top}
        <p class="eyebrow" style="margin-top:var(--space-5);text-align:left">Your innings</p>
        <div class="card" style="text-align:left">
          <strong>{top.player.name}</strong>
          <span class="muted tabular">{top.runs} ({top.balls})</span>
        </div>
      {/if}
      {#if best}
        <div class="card" style="text-align:left">
          <strong>{best.player.name}</strong>
          <span class="muted tabular">
            {figures(best.balls, best.runs, best.wickets, best.maidens)}
          </span>
        </div>
      {/if}
    {/if}
  </div>

  <div class="actions">
    <button class="btn-primary" disabled={busy} onclick={onsubmit}>
      {busy ? 'Sending…' : 'Send it in'}
    </button>
  </div>
</section>
